import { Box, Button as MuiButton, TextField } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Button } from "@src/appV2/redesign/components/Button";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { ClipboardLogo } from "@src/appV2/redesign/components/Logos";
import { Text } from "@src/appV2/redesign/components/Text";
import loginIllustration from "@src/appV2/redesign/Login/assets/login-illustration.webp";
import { useIsRebrand } from "@src/appV2/redesign/theme/RebrandProvider";
import { berlinRebrandTheme, berlinTheme } from "@src/appV2/redesign/theme/theme";
import { type ReactNode } from "react";

import { type ResetPasswordViewProps } from "./ResetPasswordView";

const PASSWORD_MIN_LENGTH = 12;

function ResetCard({
  showIllustration,
  children,
}: {
  showIllustration: boolean;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "background.baseDark",
        paddingX: 5,
        paddingTop: "max(24px, env(safe-area-inset-top))",
        paddingBottom: "max(24px, env(safe-area-inset-bottom))",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          paddingX: { xs: 5, sm: 7 },
          paddingY: 7,
          backgroundColor: "background.standout",
          borderRadius: (theme) => theme.borderRadius?.large,
          boxShadow: (theme) => theme.shadow?.card,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 5,
        }}
      >
        <ClipboardLogo height={60} width={180} />
        {showIllustration && (
          <Box component="img" src={loginIllustration} alt="" sx={{ height: 150, width: "auto" }} />
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 5,
            width: "100%",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

export function ResetPasswordViewRedesigned(props: ResetPasswordViewProps) {
  const {
    step,
    email,
    newPassword,
    confirmPassword,
    isSubmitting,
    error,
    hasActiveSession,
    onNewPasswordChange,
    onConfirmPasswordChange,
    onSubmit,
    onGoToLogin,
  } = props;

  const isRebrand = useIsRebrand();
  const theme = isRebrand ? berlinRebrandTheme : berlinTheme;

  return (
    <ThemeProvider theme={theme}>
      {step === "success" ? (
        <ResetCard showIllustration={false}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
            <Text bold variant="h5" sx={{ color: "base.base" }}>
              Password reset successfully
            </Text>
          </Box>
          <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
            Your password has been reset. You can now sign in with your new password.
          </Text>
          {!hasActiveSession && (
            <Button fullWidth variant="primary" size="large" onClick={onGoToLogin}>
              Sign in with your new password
            </Button>
          )}
        </ResetCard>
      ) : (
        <ResetCard showIllustration>
          <Text bold variant="h5" sx={{ textAlign: "center", color: "base.base" }}>
            Set new password
          </Text>
          <Text variant="body2" sx={{ color: "base.muted", textAlign: "center" }}>
            Email: {email}
          </Text>
          {error && (
            <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
              {error}
            </Text>
          )}
          <Box
            noValidate
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: 4, width: "100%" }}
            onSubmit={(formEvent: React.FormEvent) => {
              onSubmit(formEvent);
            }}
          >
            <TextField
              fullWidth
              required
              autoComplete="new-password"
              disabled={isSubmitting}
              label="New Password"
              type="password"
              value={newPassword}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "1rem",
                  borderRadius: (innerTheme) => innerTheme.borderRadius?.xLarge,
                  backgroundColor: "background.standout",
                },
              }}
              onChange={(event) => {
                onNewPasswordChange(event.target.value);
              }}
            />
            <TextField
              fullWidth
              required
              autoComplete="new-password"
              disabled={isSubmitting}
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "1rem",
                  borderRadius: (innerTheme) => innerTheme.borderRadius?.xLarge,
                  backgroundColor: "background.standout",
                },
              }}
              onChange={(event) => {
                onConfirmPasswordChange(event.target.value);
              }}
            />
            <Text variant="caption" sx={{ color: "base.muted", textAlign: "center" }}>
              Password must be at least {PASSWORD_MIN_LENGTH} characters and include uppercase and
              lowercase letters.
            </Text>
            <LoadingButton
              fullWidth
              variant="primary"
              size="large"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !newPassword || !confirmPassword || !email}
            >
              Reset Password
            </LoadingButton>
            {!hasActiveSession && (
              <MuiButton
                variant="text"
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  color: "primary.main",
                  alignSelf: "center",
                }}
                onClick={onGoToLogin}
              >
                Back to sign in with password
              </MuiButton>
            )}
          </Box>
        </ResetCard>
      )}
    </ThemeProvider>
  );
}
